import React, { useState } from "react";
import styled from "styled-components";
import { 
  IconChevronLeft, 
  IconChevronRight, 
  IconDeviceFloppy,
  IconMessageCircle,
  IconTemplate,
  IconClock,
  IconRobot
} from "@tabler/icons-react";

const AutomationRuleWizard = ({ 
  currentStep, 
  setCurrentStep, 
  children, 
  onNext, 
  onPrev, 
  onSubmit, 
  loading,
  isEditing,
  includeAutomationStep = false,
  automationType = null
}) => {
  
  // Definição das etapas do wizard
  const baseSteps = [
    { 
      title: "Tipo de Comunicação", 
      icon: IconMessageCircle,
      description: "Escolha o canal de comunicação"
    },
    { 
      title: "Template", 
      icon: IconTemplate,
      description: "Selecione o template a ser usado"
    },
    { 
      title: "Timing", 
      icon: IconClock,
      description: "Configure quando enviar"
    }
  ];

  const automationStep = {
    title: "Nova Automação", 
    icon: IconRobot,
    description: "Configure os dados básicos"
  };

  // Para automações de follow-up, pular etapas de template
  const isFollowupType = automationType === 'followup';
  const filteredBaseSteps = isFollowupType 
    ? baseSteps.filter(step => step.title === "Timing")
    : baseSteps;

  const steps = includeAutomationStep ? [automationStep, ...filteredBaseSteps] : filteredBaseSteps;

  // Calcular maxStep e minStep dinamicamente baseado no número de steps
  const maxStep = includeAutomationStep ? steps.length - 1 : steps.length;
  
  // Para automações de followup sem includeAutomationStep, começar do step 1 (timing direto)
  // Para automações normais sem includeAutomationStep, começar do step 1 (template type)
  const minStep = includeAutomationStep ? 0 : 1;

  const handleNext = async () => {
    if (onNext) {
      const canProceed = await onNext();
      if (canProceed !== false) {
        setCurrentStep(prev => Math.min(prev + 1, maxStep));
      }
    } else {
      setCurrentStep(prev => Math.min(prev + 1, maxStep));
    }
  };

  const handlePrev = () => {
    if (onPrev) {
      onPrev();
    }
    setCurrentStep(prev => Math.max(prev - 1, minStep));
  };

  const handleSubmit = () => {
    if (onSubmit) {
      onSubmit();
    }
  };

  return (
    <WizardContainer>
      {/* Header do Wizard */}
      <WizardHeader>
        <WizardTitle>
          {isEditing ? "Editar Regra de Automação" : "Nova Automação"}
        </WizardTitle>
        <WizardSubtitle>
          {isEditing 
            ? "Atualize as configurações da regra de automação."
            : "Defina automações após o evento realizado pelo agente"
          }
        </WizardSubtitle>
      </WizardHeader>

      {/* Progress Bar */}
      <ProgressContainer>
        <ProgressBar>
          <ProgressFill style={{ width: `${((currentStep - minStep) / (maxStep - minStep)) * 100}%` }} />
        </ProgressBar>
        <StepIndicator>Etapa {currentStep - minStep + 1} de {maxStep - minStep + 1}</StepIndicator>
      </ProgressContainer>

      {/* Steps Navigation */}
      <StepsNavigation>
        {steps.map((step, index) => {
          const StepIcon = step.icon;
          const stepNumber = includeAutomationStep ? index : index + 1;
          const isActive = currentStep === stepNumber;
          const isCompleted = currentStep > stepNumber;
          
          // Em mobile, mostrar apenas o step atual
          const showOnMobile = isActive;

          return (
            <StepItem 
              key={stepNumber}
              active={isActive}
              completed={isCompleted}
              showOnMobile={showOnMobile}
              onClick={() => stepNumber < currentStep && setCurrentStep(stepNumber)}
            >
              <StepIconContainer active={isActive} completed={isCompleted}>
                <StepIcon size={20} />
              </StepIconContainer>
              <StepInfo>
                <StepTitle>{step.title}</StepTitle>
                <StepDescription>{step.description}</StepDescription>
              </StepInfo>
            </StepItem>
          );
        })}
      </StepsNavigation>

      {/* Content Area */}
      <WizardContent>
        {children}
      </WizardContent>

      {/* Navigation Footer */}
      <WizardFooter>
        <MobileStepInfo>
          Etapa {currentStep - minStep + 1} de {maxStep - minStep + 1}
        </MobileStepInfo>
        
        <FooterActions>
          <SecondaryButton 
            onClick={handlePrev}
            disabled={currentStep === minStep}
          >
            <IconChevronLeft size={16} />
            <span className="d-none d-sm-inline">Anterior</span>
          </SecondaryButton>

          <DesktopStepInfo>
            Etapa {currentStep - minStep + 1} de {maxStep - minStep + 1}
          </DesktopStepInfo>

          {currentStep < maxStep ? (
            <PrimaryButton onClick={handleNext}>
              <span className="d-none d-sm-inline">Próximo</span>
              <span className="d-sm-none">Avançar</span>
              <IconChevronRight size={16} />
            </PrimaryButton>
          ) : (
            <PrimaryButton onClick={handleSubmit} disabled={loading}>
              {loading ? (
                <>
                  <LoadingSpinner />
                  <span className="d-none d-sm-inline">Salvando...</span>
                  <span className="d-sm-none">...</span>
                </>
              ) : (
                <>
                  <IconDeviceFloppy size={16} />
                  <span className="d-none d-sm-inline">{isEditing ? 'Atualizar' : 'Criar'} Regra</span>
                  <span className="d-sm-none">{isEditing ? 'Salvar' : 'Criar'}</span>
                </>
              )}
            </PrimaryButton>
          )}
        </FooterActions>
      </WizardFooter>
    </WizardContainer>
  );
};

// Styled Components
const WizardContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0;
  background: transparent;
  
  @media (min-width: 769px) {
    padding: 20px;
    background: var(--background);
  }
`;

const WizardHeader = styled.div`
  text-align: center;
  margin-bottom: 32px;
  padding: 0 16px;
  
  @media (max-width: 768px) {
    margin-bottom: 20px;
    padding: 0 8px;
  }
  
  @media (min-width: 769px) {
    padding: 0;
  }
`;

const WizardTitle = styled.h1`
  color: var(--text-primary);
  font-size: 32px;
  font-weight: 700;
  margin: 0 0 8px 0;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const WizardSubtitle = styled.p`
  color: var(--text-secondary);
  font-size: 16px;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const ProgressContainer = styled.div`
  margin-bottom: 32px;
  padding: 0 16px;
  
  @media (max-width: 768px) {
    margin-bottom: 20px;
    padding: 0 8px;
  }
  
  @media (min-width: 769px) {
    padding: 0;
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: var(--border-color);
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 8px;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: var(--primary);
  transition: width 0.3s ease;
  border-radius: 4px;
`;

const StepIndicator = styled.div`
  text-align: center;
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 500;
`;

const StepsNavigation = styled.div`
  display: flex;
  justify-content: center;
  gap: 24px;
  margin-bottom: 32px;
  flex-wrap: wrap;
  padding: 0 16px;

  @media (max-width: 768px) {
    gap: 12px;
    margin-bottom: 20px;
    padding: 0 8px;
    justify-content: center;
  }
  
  @media (min-width: 769px) {
    padding: 0;
  }
`;

const StepItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: ${props => props.completed ? 'pointer' : 'default'};
  opacity: ${props => props.active ? 1 : props.completed ? 0.8 : 0.5};
  transition: opacity 0.2s ease;
  min-width: 120px;
  flex: 1;

  &:hover {
    opacity: ${props => props.completed ? 1 : props.active ? 1 : 0.5};
  }

  @media (max-width: 768px) {
    display: ${props => props.showOnMobile ? 'flex' : 'none'};
    min-width: auto;
    max-width: none;
    justify-content: center;
    width: 100%;
  }
`;

const StepIconContainer = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
  background: ${props => 
    props.active 
      ? 'var(--primary)' 
      : props.completed 
        ? 'var(--success)' 
        : 'var(--surface)'
  };
  color: ${props => 
    props.active || props.completed 
      ? 'white' 
      : 'var(--text-secondary)'
  };
  border: 2px solid ${props => 
    props.active 
      ? 'var(--primary)' 
      : props.completed 
        ? 'var(--success)' 
        : 'var(--border-color)'
  };
  transition: all 0.2s ease;

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    margin-bottom: 8px;
  }
`;

const StepInfo = styled.div`
  text-align: center;
`;

const StepTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: var(--text-primary);
  margin-bottom: 4px;

  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

const StepDescription = styled.div`
  font-size: 12px;
  color: var(--text-secondary);

  @media (max-width: 768px) {
    font-size: 11px;
  }
`;

const WizardContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 32px;
  box-shadow: var(--shadow-sm);
  margin-bottom: 32px;

  @media (max-width: 768px) {
    padding: 20px;
    border-radius: 12px;
    margin-bottom: 20px;
    margin-left: -4px;
    margin-right: -4px;
    box-shadow: none;
    border: 1px solid var(--border-color);
  }
`;

const WizardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 16px;
  background: white;
  border-top: 1px solid var(--border-color);
  margin-top: 20px;

  @media (max-width: 768px) {
    position: sticky;
    bottom: 0;
    padding: 16px;
    margin-left: -20px;
    margin-right: -20px;
    margin-bottom: -20px;
    box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
    z-index: 100;
  }
  
  @media (min-width: 769px) {
    padding: 20px 0;
    background: transparent;
    border-top: none;
    margin-top: 0;
    box-shadow: none;
  }
`;

const SecondaryButton = styled.button`
  background: var(--surface);
  color: var(--text-secondary);
  border: 2px solid var(--border-color);
  border-radius: 8px;
  padding: 12px 24px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  justify-content: center;

  &:hover:not(:disabled) {
    background: var(--surface-hover);
    border-color: var(--border-color-hover);
    color: var(--text-primary);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    flex: 1;
    padding: 16px 20px;
  }
`;

const PrimaryButton = styled.button`
  background: var(--primary);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  justify-content: center;

  &:hover:not(:disabled) {
    background: var(--primary-hover);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 768px) {
    flex: 2;
    padding: 16px 20px;
  }
`;

const LoadingSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// Novos componentes mobile-first para o footer
const FooterActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  width: 100%;
  
  @media (max-width: 768px) {
    justify-content: space-between;
    gap: 12px;
  }
`;

const MobileStepInfo = styled.div`
  text-align: center;
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 12px;
  
  @media (min-width: 769px) {
    display: none;
  }
`;

const DesktopStepInfo = styled.div`
  text-align: center;
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 500;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

export default AutomationRuleWizard;